import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { IconFolder, IconFolderPlus } from '@rossum/ui/icons/tabler';
import {
  Button,
  Chip,
  CircularProgress,
  Popover,
  Stack,
  SvgIcon,
} from '@rossum/ui/material';
import { useWorkspacesWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';
import { QueueSelectList } from '../../queues/select-queue/QueueSelectList';

export const SelectQueueChip = ({
  selectedQueues,
  setSelectedQueues,
  disabled,
  dataCy,
}: {
  selectedQueues: number[];
  setSelectedQueues: (queue: number[]) => void;
  disabled?: boolean;
  dataCy?: string;
}) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { workspacesWithQueues, isLoading } = useWorkspacesWithQueues({
    enableQueries: true,
  });

  const queuesByWorkspace = useMemo<
    Array<{
      id: number;
      name: string;
      queues: Array<{ id: number; name: string }>;
    }>
  >(
    () =>
      workspacesWithQueues?.map(workspace => ({
        id: workspace.id,
        name: workspace.name,
        queues: workspace.queues.map(queue => ({
          id: queue.id,
          name: queue.name,
        })),
      })) ?? [],
    [workspacesWithQueues]
  );

  const handleSelectQueues = useCallback(
    (ids: number[]) => {
      setSelectedQueues(ids);
    },
    [setSelectedQueues]
  );

  const selectedItems = useMemo(() => {
    const firstWorkspace = queuesByWorkspace.find(workspace =>
      workspace.queues.some(queue => selectedQueues.includes(queue.id))
    );

    const firstQueue = firstWorkspace?.queues.find(queue =>
      selectedQueues.includes(queue.id)
    );

    return {
      firstSelectedWorkspaceName: firstWorkspace?.name,
      firstSelectedQueueNameInThatWorkspace: firstQueue?.name,
    };
  }, [queuesByWorkspace, selectedQueues]);

  const allSelected = useMemo(() => {
    return queuesByWorkspace.every(workspace =>
      workspace.queues.every(queue => selectedQueues.includes(queue.id))
    );
  }, [queuesByWorkspace, selectedQueues]);

  return (
    <>
      {isLoading ? (
        <Chip
          disabled={disabled || isLoading || !queuesByWorkspace.length}
          label={intl.formatMessage({
            id: 'features.rules.selectQueueChip.selectQueue',
          })}
          icon={<CircularProgress size={12} />}
          variant="filled"
        />
      ) : (
        <Chip
          disabled={disabled || isLoading}
          data-cy={dataCy}
          label={
            selectedQueues.length === 1
              ? `${selectedItems.firstSelectedQueueNameInThatWorkspace} (${selectedItems.firstSelectedWorkspaceName})`
              : allSelected
                ? intl.formatMessage({
                    id: 'features.rules.selectQueueChip.allQueuesSelected',
                  })
                : selectedQueues.length > 1
                  ? intl.formatMessage(
                      {
                        id: 'features.rules.selectQueueChip.multipleQueuesSelected',
                      },
                      {
                        count: selectedQueues.length,
                      }
                    )
                  : intl.formatMessage({
                      id: 'features.rules.selectQueueChip.selectQueue',
                    })
          }
          icon={
            <SvgIcon fontSize="medium">
              {selectedQueues.length > 0 ? <IconFolder /> : <IconFolderPlus />}
            </SvgIcon>
          }
          onClick={(event: MouseEvent<HTMLDivElement>) => {
            if (disabled) return;
            setAnchorEl(event.currentTarget);
          }}
          variant="filled"
        />
      )}
      {queuesByWorkspace.length > 0 ? (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Stack spacing={1} p={2} sx={{ width: '400px' }}>
            <Stack py={1} maxHeight="40vh" overflow="auto">
              <QueueSelectList
                topSelections={selectedQueues}
                queuesByWorkspace={queuesByWorkspace}
                onSelect={handleSelectQueues}
                selectedQueues={selectedQueues}
                isLoading={isLoading}
                multiSelect
                autoFocus
              />
            </Stack>
            <Stack direction="row" spacing={2} alignSelf="flex-end">
              <Button
                onClick={() => {
                  if (allSelected) {
                    handleSelectQueues([]);
                  } else {
                    handleSelectQueues(
                      queuesByWorkspace.flatMap(workspace =>
                        workspace.queues.map(queue => queue.id)
                      )
                    );
                  }
                }}
                variant="outlined"
                color="secondary"
              >
                {allSelected
                  ? intl.formatMessage({
                      id: 'features.rules.selectQueue.deselectAll.button.label',
                    })
                  : intl.formatMessage({
                      id: 'features.rules.selectQueue.selectAll.button.label',
                    })}
              </Button>
              <Button
                onClick={() => setAnchorEl(null)}
                variant="contained"
                color="primary"
              >
                {intl.formatMessage({
                  id: 'features.rules.selectQueue.apply.button.label',
                })}
              </Button>
            </Stack>
          </Stack>
        </Popover>
      ) : null}
    </>
  );
};
